import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StarRating from '../../../components/ui/StarRating';
import RewardBadge from '../../../components/ui/RewardBadge';

const TaskCard = ({ task, onEdit, onDelete, onToggleComplete }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'in-progress':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'on-hold':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return { name: 'alert-circle', color: '#EF4444' };
      case 'medium':
        return { name: 'alert-triangle', color: '#F59E0B' };
      case 'low':
        return { name: 'info', color: '#10B981' };
      default:
        return { name: 'minus', color: '#6B7280' };
    }
  };

  const priorityIcon = getPriorityIcon(task?.priority);
  const isOverdue = new Date(task.dueDate) < new Date() && task?.status !== 'completed';

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 hover:shadow-warm-md transition-smooth">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={() => onToggleComplete(task?.id)}
            className="flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-smooth mt-1"
            style={{
              borderColor: task?.assignedColor,
              backgroundColor: task?.status === 'completed' ? task?.assignedColor : 'transparent'
            }}
            aria-label={task?.status === 'completed' ? 'Mark as incomplete' : 'Mark as complete'}
          >
            {task?.status === 'completed' && (
              <Icon name="check" size={14} color="#FFFFFF" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <h3 className={`text-base md:text-lg font-semibold text-foreground mb-2 ${task?.status === 'completed' ? 'line-through opacity-60' : ''}`}>
              {task?.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {task?.description}
            </p>

            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium"
                style={{
                  backgroundColor: `${task?.assignedColor}15`,
                  color: task?.assignedColor
                }}
              >
                <Icon name="user" size={14} />
                <span>{task?.assignedTo}</span>
              </div>

              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium border ${getStatusColor(task?.status)}`}>
                <Icon name="activity" size={14} />
                <span className="capitalize">{task?.status?.replace('-', ' ')}</span>
              </div>

              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs md:text-sm font-medium bg-muted text-muted-foreground">
                <Icon name={priorityIcon?.name} size={14} color={priorityIcon?.color} />
                <span className="capitalize">{task?.priority}</span>
              </div>

              {task?.stars > 0 && (
                <div className="flex items-center gap-1.5">
                  <StarRating value={task?.stars} readonly size={16} />
                </div>
              )}

              {task?.reward && (
                <RewardBadge reward={task?.reward} size="sm" />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(task)}
            aria-label="Edit task"
          >
            <Icon name="pencil" size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(task?.id)}
            aria-label="Delete task"
          >
            <Icon name="trash-2" size={18} />
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="calendar" size={16} />
            <span>Due: {new Date(task.dueDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            {isOverdue && (
              <span className="text-destructive font-medium">(Overdue)</span>
            )}
          </div>
          <span className="text-muted-foreground">{task?.progress}%</span>
        </div>

        <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-smooth"
            style={{
              width: `${task?.progress}%`,
              backgroundColor: task?.assignedColor
            }}
          />
        </div>

        {task?.milestones && task?.milestones?.length > 0 && (
          <div className="pt-3 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="flag" size={14} className="text-muted-foreground" />
              <span className="text-xs md:text-sm font-medium text-foreground">Milestones</span>
            </div>
            <div className="space-y-1.5">
              {task?.milestones?.map((milestone, index) => (
                <div key={index} className="flex items-center gap-2 text-xs md:text-sm">
                  <Icon
                    name={milestone?.completed ? 'check-circle-2' : 'circle'}
                    size={14}
                    color={milestone?.completed ? task?.assignedColor : '#9CA3AF'}
                  />
                  <span className={milestone?.completed ? 'line-through text-muted-foreground' : 'text-foreground'}>
                    {milestone?.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {task?.category && (
          <div className="flex items-center gap-2 pt-2">
            <Icon name="tag" size={14} className="text-muted-foreground" />
            <span className="text-xs md:text-sm text-muted-foreground">{task?.category}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCard;